// ============================================================================
// LoginAside — the brand / "show, don't tell" panel beside the sign-in form.
// ----------------------------------------------------------------------------
// MOBILE-FIRST: at 390 this is a compact brand band ABOVE the form (graphic +
// one-line value prop), so the smallest screens lead with the form and never
// scroll past a tall hero to reach it. From lg up it becomes the full left
// stage — the looping <LoginPulse/> graphic, a floating live KPI card, the
// copy, and a proof strip — mirroring the landing aesthetic.
//
// Built ENTIRELY on the locked Pulse design system (Badge, StatTile, tokens)
// and Framer Motion, all reduced-motion aware. Decorative; the form carries the
// real task. Reuses the optional-first session probe only to warmly greet a
// returning trainer — the panel is fully fine with no session.
// ============================================================================
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Badge, StatTile } from "../../components";
import { LoginPulse } from "./LoginPulse";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.06 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

export function LoginAside() {
  const { t } = useTranslation("login");
  const reduce = useReducedMotion();

  const proof = [
    t("aside.proof.clients"),
    t("aside.proof.calendar"),
    t("aside.proof.metrics"),
    t("aside.proof.reports"),
  ];

  return (
    <section className="relative isolate flex flex-col justify-center overflow-hidden bg-paper px-gutter py-8 text-body lg:min-h-screen lg:py-12">
      {/* Atmosphere: soft coral + violet radial washes + a masked grid. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(720px 420px at 86% -6%, rgba(255,138,61,0.18), transparent 60%)," +
            "radial-gradient(640px 460px at -6% 20%, rgba(123,91,255,0.16), transparent 58%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-grid-light bg-grid"
        style={{
          maskImage:
            "radial-gradient(ellipse 80% 70% at 40% 10%, #000 26%, transparent 72%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 70% at 40% 10%, #000 26%, transparent 72%)",
        }}
      />

      <motion.div
        variants={reduce ? undefined : stagger}
        initial={reduce ? undefined : "hidden"}
        animate={reduce ? undefined : "show"}
        className="mx-auto w-full max-w-md lg:mx-0 lg:max-w-lg"
      >
        <motion.div variants={reduce ? undefined : rise}>
          <Badge tone="volt" solid dot className="mb-5">
            {t("badge")}
          </Badge>
        </motion.div>

        {/* The looping brand graphic + a floating live KPI card. */}
        <motion.div
          variants={reduce ? undefined : rise}
          className="relative mx-auto w-full max-w-sm lg:mx-0 lg:max-w-md"
        >
          <LoginPulse className="mx-auto block h-auto w-full max-w-[400px]" />

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 24, rotate: -2 }}
            animate={{ opacity: 1, y: 0, rotate: reduce ? 0 : -2 }}
            transition={{ duration: 0.7, delay: 0.4, ease: EASE }}
            className="relative z-10 mx-auto -mt-2 w-full max-w-[15rem] sm:absolute sm:-bottom-4 sm:right-0 sm:mt-0"
          >
            <div className="rounded-xl bg-surface p-4 shadow-lg ring-1 ring-line">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-display text-sm font-bold text-body">
                  {t("aside.live.title")}
                </span>
                <Badge tone="success" dot>
                  {t("aside.live.status")}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <StatTile
                  tone="volt"
                  label={t("aside.live.sessions")}
                  value={18}
                  delta="+3"
                  deltaTone="up"
                  className="!p-3"
                />
                <StatTile
                  accent="violet"
                  label={t("aside.live.rpe")}
                  value={7.4}
                  decimals={1}
                  delta="+0.6"
                  className="!p-3"
                />
              </div>
            </div>
            <div
              aria-hidden
              className="absolute -right-2.5 -top-2.5 -z-10 h-20 w-20 rotate-12 rounded-xl bg-grad-violet shadow-ember"
            />
          </motion.div>
        </motion.div>

        {/* Copy — hidden on the smallest screens so mobile leads with the form,
            revealed from sm up where there's room to breathe. */}
        <motion.h2
          variants={reduce ? undefined : rise}
          className="mt-10 hidden font-display font-extrabold tracking-tightest leading-[1.02] text-[clamp(1.9rem,1.3rem+2.4vw,3rem)] sm:block"
        >
          <span className="block">{t("aside.title")}</span>
        </motion.h2>
        <motion.p
          variants={reduce ? undefined : rise}
          className="mt-4 hidden max-w-md text-base leading-relaxed text-body-muted sm:block"
        >
          {t("aside.subtitle")}
        </motion.p>

        {/* Proof chips — the four pillars. */}
        <motion.ul
          variants={reduce ? undefined : rise}
          className="mt-7 hidden flex-wrap gap-2 sm:flex"
        >
          {proof.map((word) => (
            <li
              key={word}
              className="inline-flex items-center gap-1.5 rounded-pill bg-surface px-3 py-1.5 font-display text-xs font-bold uppercase tracking-kicker text-body-soft shadow-xs ring-1 ring-line"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-coral" aria-hidden />
              {word}
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
}
